import { useEffect, useState } from "react";
import { cx } from "../ui/primitives";

/**
 * Page a list in fixed-size chunks. `pad` is how many blank cells the last page
 * needs to keep the grid a constant height — otherwise the pager jumps up under
 * the cursor when you land on a short page.
 */
export function usePaged<T>(items: readonly T[], pageSize: number) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));

  // Deleting the last item on the last page must not strand us past the end.
  useEffect(() => {
    setPage((p) => Math.min(p, pages - 1));
  }, [pages]);

  const safePage = Math.min(page, pages - 1);
  const start = safePage * pageSize;
  const shown = items.slice(start, start + pageSize);

  return { page: safePage, pages, setPage, shown, pad: pageSize - shown.length };
}

export function Pager({
  page,
  pages,
  onPage,
  label,
}: {
  page: number;
  pages: number;
  onPage: (page: number) => void;
  /** Names what's being paged, for screen readers: "layouts", "views". */
  label: string;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3">
      <PagerButton label={`Previous ${label}`} disabled={page === 0} onClick={() => onPage(page - 1)}>
        ←
      </PagerButton>
      <span className="text-[11px] tabular-nums text-zinc-500">
        {page + 1} / {pages}
      </span>
      <PagerButton
        label={`More ${label}`}
        disabled={page >= pages - 1}
        onClick={() => onPage(page + 1)}
      >
        →
      </PagerButton>
    </div>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cx(
        "flex h-7 w-7 items-center justify-center rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] text-sm text-zinc-300 transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-35",
        "enabled:hover:border-[var(--color-accent)]/60 enabled:hover:text-zinc-100",
      )}
    >
      {children}
    </button>
  );
}
